import logoVariants from '@/assets/img/logo';
import CustomButton from '@/components/atoms/CustomButton';
import LoadingLogo from '@/components/atoms/LoadingLogo';
import ModalContainer from '@/components/molecules/ModalContainer/ModalContainer';
import useSearchOrganizations from '@/hooks/super-admin/useSearchOrganizations';
import { useDebounce } from '@/hooks/useDebounce';

import { Center, HStack, Image, Input, Text, useDisclosure, VStack } from '@chakra-ui/react';
import React, { useState } from 'react';
import ConfirmOrganizationSelectionModal from './components/ConfirmOrganizationSelectionModal';

const ORG_LOGO_SIZE = 48;

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

const SelectOrganizationContextModal = ({ isOpen, onClose }: Props) => {
  const [localSearchText, setLocalSearchText] = useState('');
  const debouncedSearch = useDebounce(localSearchText, 300);

  const [selectedOrganization, setSelectedOrganization] = useState<{
    id: string;
    name: string;
    logoUrl?: string;
  } | null>(null);

  const { isOpen: isConfirmOpen, onOpen: onConfirmOpen, onClose: onConfirmClose } = useDisclosure();

  const { data, isLoading } = useSearchOrganizations(localSearchText, !!debouncedSearch);

  return (
    <ModalContainer isOpen={isOpen} onClose={onClose} title="Find Organization" overflowY="hidden">
      <Input
        className="custom-input"
        backgroundColor={'white'}
        placeholder="Search..."
        _placeholder={{ textOverflow: 'ellipsis', overflow: 'hidden' }}
        value={localSearchText}
        onChange={(e) => setLocalSearchText(e.target.value)}
        _disabled={{ opacity: 0.8, cursor: 'not-allowed' }}
      />
      <VStack my={'20px'} width={'100%'} alignItems={'center'}>
        <LoadingLogo isLoading={isLoading} spinnerWidth="64px" />
      </VStack>
      <VStack height="600px" overflowY="scroll" width={'100%'} alignItems={'center'} px={'20px'} py={'12px'}>
        {data && data.data.length > 0 ? (
          <VStack spacing={'10px'} align="stretch" width={'100%'}>
            {data.data.map((organization) => (
              <HStack
                gap={'20px'}
                bg="white"
                key={organization.id}
                boxShadow={' 0px 8px 34px 0px #0041791A'}
                _hover={{ boxShadow: '0px 8px 34px 0px #00417933', cursor: 'pointer' }}
                borderRadius={'20px'}
                padding={'16px'}
              >
                <HStack flex={1} alignItems={'center'} justifyContent={'flex-start'} gap={'20px'}>
                  {organization?.logoUrl ? (
                    <Image
                      alt="user-avatar-wellness"
                      width={`${ORG_LOGO_SIZE}px`}
                      maxHeight={`${ORG_LOGO_SIZE}px`}
                      src={organization?.logoUrl}
                      style={{ objectFit: 'cover' }} // borderRadius: '50%',
                    />
                  ) : (
                    <Center
                      borderRadius={'50%'}
                      border={'5px solid white'}
                      boxShadow={'0px 4px 8px 0px #0E00621A'}
                      width={`${ORG_LOGO_SIZE}px`}
                      height={`${ORG_LOGO_SIZE}px`}
                      backgroundColor={'primary.100'}
                    >
                      <Image
                        src={logoVariants.purpleNoText112x112}
                        width={'30px'}
                        height={'30px'}
                        alt="my-omnia-logo"
                      />
                    </Center>
                  )}

                  <Text variant={'urbanistSemiBold'} color={'text.mediumBlue'} fontSize={'20px'} textAlign={'left'}>
                    {organization.name}
                  </Text>
                </HStack>
                <CustomButton
                  onClick={() => {
                    setSelectedOrganization(organization);
                    onConfirmOpen();
                  }}
                  label="Select"
                />
              </HStack>
            ))}
          </VStack>
        ) : (
          <Text variant={'urbanistSemiBold'} marginY={'30px'} color={'text.mediumBlue'} fontSize={'20px'}>
            {localSearchText == '' ? 'Type in to start searching...' : 'No results found'}
          </Text>
        )}
      </VStack>
      <ConfirmOrganizationSelectionModal
        isOpen={isConfirmOpen}
        onClose={onConfirmClose}
        organization={selectedOrganization}
      />
    </ModalContainer>
  );
};

export default SelectOrganizationContextModal;
